import { useEffect, useState } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect, RootStateOrAny } from 'react-redux';
import moment from 'moment';

import { Button, Input, Select, Checkbox, Form, Alert, Modal } from 'antd';

import { ErrorResponse } from 'utils/types';
import notifier from 'utils/notifier';
import { selectDefaultProvider } from 'utils/helpers';
import { ProviderType } from 'utils/enums';

import { getUserAcknowledgement, createUserAcknowledgement } from 'store/app/actions';

import FloatLabel from 'components/Common/FloatLabel';
import DatePickerLocalised from 'components/Common/DatePickerLocalised';
import { MediCalPopUpContent } from './Modals';

import { ProviderList, SearchFormProps } from './Proptypes';

import './index.scss';

const { Option } = Select;

// APIs
/*

SEARCH MEMBER FORM
  (init)
  - App/GetPortalVersion
  - Parameter?parameter=ImageExtension
  - AuthMember/GetAuthSearchProviderList

   (On Submit)
  - AuthMember/GetAuthMember

*/

const SelectMemberForm = ({ retroDos, setRetroDos, ...props }: SearchFormProps) => {

  const [form] = Form.useForm();
  const [displayRetroDOS, setDisplayRetroDOS] = useState(false);
  const [visible, setVisible] = useState(false);
  const [birth, setBirth] = useState('');

  useEffect(() => {
    getUserAcknowledgement();
  }, []);

  //sets the provider dropdown to the login user as default provider
  useEffect(() => {
    if (props.providerList.length > 0) {

      props.setProvider(selectDefaultProvider(props.providerList, {})); //this is used for the parse data when calling the API

      //this is to pre-populate the form default provider
      form.setFieldsValue({
        provider: selectDefaultProvider(props.providerList, undefined, 'string')
      });
    }
  }, [props.providerList])


  useEffect(() => {
    birth !== '' && form.validateFields();
  }, [birth]);

  const getUserAcknowledgement = () => {
    return props.getUserAcknowledgement(sessionStorage.getItem('user_id')!)
      .then((response: boolean) => {
        if (!response) {
          Modal.confirm({
            title: 'Medi-Cal Rx Notice',
            content: <MediCalPopUpContent />,
            width: '1500px',
            maskClosable: false,
            cancelButtonProps: { style: { display: 'none' } },
            okText: 'Submit',
            okButtonProps: { loading: props.isFetchingUserAck || props.isCreatingUserAck },
            onOk: () => {
              const payload = {
                RmiUserId: sessionStorage.getItem('user_id')!,
                RmiCompanyId: props.provider ? props.provider.RmiCompanyId : sessionStorage.getItem('ipaGroup')
              }
              props.createUserAcknowledgement(payload)
                .catch((err: ErrorResponse) => {
                  notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
                });
            }
          })
        }
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const validationRules = async (fieldData: any, value: any) => {
    const charLessThanFive = (value: any) => fieldData.field === 'memberId' && value && value.trim().length < 5;

    const allEmptyFields = () => {
      return !form.getFieldValue('firstName') && !form.getFieldValue('lastName') && !form.getFieldValue('memberId') && !birth;
    }

    if (form.getFieldValue('provider')) {

      if (charLessThanFive(value)) {
        return Promise.reject('Please enter at least 5 characters.');
      }

      // PCP (P) / Clinic (C) validation
      if ([ProviderType.PCP, ProviderType.CLINIC].includes(props.provider.RmiClass)) {

        if (allEmptyFields()) {
          setVisible(true);
          return Promise.reject('Required');
        }

        if (fieldData.field === 'memberId') {
          const memberIdResolved = () => {
            return form.getFieldValue('memberId');
          }

          if (memberIdResolved()) {
            setVisible(false);
            return Promise.resolve();
          }
        }

        if (fieldData.field === 'firstName') {
          const firstNameResolved = () => {
            return form.getFieldValue('firstName');
          }

          const missingFirstName = () => {
            return !form.getFieldValue('firstName') && form.getFieldValue('lastName');
          }

          if (firstNameResolved()) {
            setVisible(false);
            return Promise.resolve();
          }

          if (missingFirstName()) {
            setVisible(false);
            return Promise.reject('Required');
          }
        }

        if (fieldData.field === 'lastName') {

          const lastNameResolved = () => {
            return form.getFieldValue('lastName');
          }

          const missingLastName = () => {
            return form.getFieldValue('firstName') && !form.getFieldValue('lastName');
          }

          if (lastNameResolved()) {
            setVisible(false);
            return Promise.resolve();
          }

          if (missingLastName()) {
            return Promise.reject('Required');
          }
        }

        if (fieldData.field === "birth") {
          const DOBResolved = () => {
            return (birth
              || form.getFieldValue("lastName")
              || form.getFieldValue("firstName")
              || form.getFieldValue("memberId"));
          };
          const missingDOB = () => {
            return (!birth);
          };
          if (DOBResolved()) {
            setVisible(false);
            return Promise.resolve();
          }
          if (missingDOB()) {
            return Promise.reject("Required");
          }
        }

      } else {

        //Specialist, ALL PROVIDERS
        if (fieldData.field === 'memberId') {
          const memberIdResolved = () => {
            return (form.getFieldValue('firstName') && form.getFieldValue('lastName') && birth)
            || (birth && form.getFieldValue('memberId'));
          }

          const missingDOB = () => {
            return birth && !form.getFieldValue('memberId');
          }

          if (allEmptyFields()) {
            setVisible(true);
            return Promise.reject('Required');
          }

          if (memberIdResolved()) {
            setVisible(false);
            return Promise.resolve();
          }

          if (missingDOB()) {
            setVisible(false);
            return Promise.reject('Required');
          }
        }

        if (fieldData.field === 'firstName') {
          const missingLastName = () => {
            return (!form.getFieldValue('firstName') || form.getFieldValue('lastName')) && !form.getFieldValue('memberId');
          }

          const firstNameResolved = () => {
            return (form.getFieldValue('firstName') && form.getFieldValue('lastName')) || (birth && (form.getFieldValue('firstName') && form.getFieldValue('lastName')));
          }

          if (allEmptyFields()) {
            setVisible(true);
            return Promise.reject('Required');
          }

          else if (firstNameResolved()) {
            setVisible(false);
            return Promise.resolve();
          }

          else if (missingLastName()) {
            setVisible(false);
            return Promise.reject('Required');
          }
        }

        if (fieldData.field === 'lastName') {
          const lastNameResolved = () => {
            return (form.getFieldValue('firstName') && form.getFieldValue('lastName')) || (birth && (form.getFieldValue('firstName') && form.getFieldValue('lastName')));
          }

          const missingMemberId = () => {
            return (form.getFieldValue('firstName') || !form.getFieldValue('lastName')) && !form.getFieldValue('memberId');
          }

          if (allEmptyFields()) {
            setVisible(true);
            return Promise.reject('Required');
          }

          else if (lastNameResolved()) {
            setVisible(false);
            return Promise.resolve();
          }

          else if (missingMemberId()) {
            return Promise.reject('Required');
          }
        }

        if (fieldData.field === 'birth') {
          const missingDOB = () => {
            return !birth && form.getFieldValue('memberId');
          }

          const missingFirstName = () => {
            return (form.getFieldValue('firstName') || !form.getFieldValue('lastName')) && !birth;
          }

          const missingLastName = () => {
            return (!form.getFieldValue('firstName') || form.getFieldValue('lastName')) && !birth;
          }


          if (allEmptyFields()) {
            setVisible(true);
            return Promise.reject('Required');
          }

          else if (birth) {
            setVisible(false);
            return Promise.resolve();
          }

          else if (missingDOB()) {
            setVisible(false);
            return Promise.reject('Required');
          }

          else if (missingFirstName()) {
            setVisible(false);
            return Promise.reject('Required');
          }

          else if (missingLastName()) {
            setVisible(false);
            return Promise.reject('Required');
          }
        }
      }
    } else {
      setVisible(false);
    }
  };

  const retroCheckboxChange = () => {
    setRetroDos('');
    form.resetFields(['retroDOS']);
    setDisplayRetroDOS(!displayRetroDOS);
  };

  const onSearch = () => {
    form.validateFields().then(values => {

      const provider = JSON.parse(values.provider || '{}');
      let form_data = {

        AuthMemberSearchModel: {
          "CompanyId": provider.RmiCompanyId,
          "MemberId": values.memberId ? values.memberId : null,
          "DateOfBirth": birth !== '' ? birth : null,
          "FirstName": values.firstName ? values.firstName : null,
          "LastName": values.lastName ? values.lastName : null,
          "ProvKeyId": provider.RmiProvKeyId,
          "Provider": provider,
          "RetroDOS": retroDos !== '' ? retroDos : null,
          "PageIndex": props.tablePagination.current || 1,
          "PageSize": props.tablePagination.pageSize || 20,
        },
        UserIdModel: {
          "UserId": sessionStorage.user_id,
          "UserRole": sessionStorage.user_role,
        }
      };
      localStorage.setItem("print_ipa", provider.RmiCompanyId);
      props.getAuthMember(form_data)
        .then((response: any[]) => {
          if (response.length === 0) {
            notifier.error(' There were no results for this search. Please check the fields and try again.');
            return;
          }

          props.setAccessErrors([]);
          props.updateTableData(response);
        })
        .catch((err: ErrorResponse) => {
            notifier.error(err.response?.data.Message || 'Something went wrong, please contact a system administrator.');
        });
    });
  };

  const onReset = () => {
    props.setProvider({});
    setDisplayRetroDOS(false);
    props.updateTableData([]);
    setBirth('');
    setRetroDos('');
    form.resetFields();
    setVisible(false);
    props.setAccessErrors([]);
  };

  return (
    <>
    <Form form={form} name={'authSearchMemberForm'} onFinish={onSearch}>
      <div className="member-search row flex-lg-wrap justify-content-start mb-3 ml-0">
        <div className="col-lg-12 col-md-4">
          <FloatLabel
            label="Select a Provider:"
            name="provider"
            form={form}
            value={props.provider}
            rules={[{ required: true, message: 'Please select provider.' }]}
          >
            <Select
              showSearch
              value={props.provider}
              placeholder="Select a Provider..."
              onChange={value => props.setProvider(JSON.parse(value))}
              loading={props.isFetching}
            >
              {props.providerList.map((option: ProviderList, key: number) => {
                  return <Option key={key} value={JSON.stringify(option)}>{option.Provider}</Option>;
              })}
            </Select>
          </FloatLabel>
        </div>

        <div className="col-lg-6 col-md-4">
          <FloatLabel
            label="Member ID:"
            name="memberId"
            form={form}
            dependencies={['firstName', 'lastName', 'birth']}
            validateTrigger={['onSubmit']}
            rules={[
              () => ({
                validator: validationRules
              })
            ]}
          >
            <Input />
          </FloatLabel>
        </div>

        <div className="col-lg-6 col-md-4">
          <FloatLabel
            label="DOB (MM/DD/YYYY)"
            name="birth"
            form={form}
            value={birth}
            validateTrigger={['onSubmit']}
            dependencies={['firstName', 'lastName', 'memberId']}
            rules={[
              () => ({
                validator: validationRules
              }),
              () => ({
                async validator() {
                  const validDate = () => birth && moment(birth).isValid() && moment(birth).year() >= 1900 && birth.match(/^\d{2}\/\d{2}\/\d{4}$/);
                  if (validDate()) {
                    return Promise.resolve();
                  }

                  // DOB is not required and can blank
                  if (!visible && birth) {
                    return Promise.reject(new Error('Invalid Date'));
                  }
                }
              })
            ]}
          >
            <DatePickerLocalised
              date={birth}
              setDate={setBirth}
              allowClear
            />
          </FloatLabel>
        </div>

        <div className="col-lg-6 col-md-4">
          <FloatLabel
            label="Last Name"
            name="lastName"
            form={form}
            validateTrigger={['onSubmit']}
            dependencies={['firstName', 'memberId', 'birth']}
            rules={[
              () => ({
                validator: validationRules
              })
            ]}
          >
          <Input />
          </FloatLabel>
        </div>

        <div className="col-lg-6 col-md-4">
          <FloatLabel
            label="First Name"
            name="firstName"
            form={form}
            validateTrigger={['onSubmit']}
            dependencies={['memberId', 'lastName', 'birth']}
            rules={[
              () => ({
                validator: validationRules
              })
            ]}
          >
          <Input />
          </FloatLabel>
        </div>

        <div className="col-lg-1 col-md checkbox-padding">
          <Checkbox
            checked={displayRetroDOS}
            onChange={retroCheckboxChange}
          />
        </div>

        <div className="col-lg-5 col-md">
          <FloatLabel
            label="Retro DOS"
            name="retroDOS"
            form={form}
            value={retroDos}
            validateTrigger={['onchange']}
            rules={[
              () => ({
                async validator(_: any, __: any) {
                  const invalidDate = () => !retroDos || !moment(retroDos).isValid() || !retroDos.match(/^\d{2}\/\d{2}\/\d{4}$/);
                  if (displayRetroDOS && invalidDate()) {
                    return Promise.reject(new Error('Invalid Date'));
                  }

                  if (retroDos && moment(retroDos).isBefore(moment().subtract(90, 'days'), 'day')) {
                    return Promise.reject(new Error('Retro date cannot be older than 90 days'));
                  } else if (retroDos && moment(retroDos).isAfter(moment(), 'day')) {
                    return Promise.reject(new Error('Retro date cannot be in the future'));
                  }
                  return Promise.resolve();
                },
              })
            ]}
          >
            <DatePickerLocalised
              date={retroDos}
              setDate={setRetroDos}
              disabled={!displayRetroDOS}
            />
          </FloatLabel>
        </div>

        <div className="col-lg-6 col-md-6 mt-2 mb-4 btn-wrapper">
          <Button type="primary" htmlType={'submit'} loading={props.isSearching} style={{ width: '50%' }}>SEARCH</Button>
          <Button type="default" onClick={onReset} style={{ width: '50%' }}>RESET</Button>
        </div>
      </div>

      {visible ? (
        <Alert
          message={[ProviderType.PCP, ProviderType.CLINIC].includes(props.provider?.RmiClass) ?
            <>
              Please meet one of the following requirements:<br />
                MemberID ONLY <br />
                DOB ONLY <br/>
                Last Name + First Name ONLY
            </>
           :
            <>
              Please meet one of the following requirements:<br />
              MemberID + DOB (mm/dd/yyyy) <br />
              Last Name + First Name + DOB (mm/dd/yyyy)
            </>
          }
          type="error"
          className="col-lg-24"
        />
      ) : null}

        {props.accessErrors?.length > 0 ? (
          <Alert
            message={
              <>
                {props.accessErrors.map((x: string) => {
                  return x;
                })}
              </>
            }
          />
        ) : null}
    </Form>
  </>
  );
};

const mapStateToProps = (store: RootStateOrAny) => ({
  errorMsg: store.rootReducer.authProvider.errorMsg,
  isFetching: store.rootReducer.authProvider.isFetching,
  isSearching: store.rootReducer.authMember.isFetching,
  isFetchingUserAck: store.rootReducer.app.isFetchingUserAck,
  isCreatingUserAck: store.rootReducer.app.isCreatingUserAck,
  isCreatingGenProv: store.rootReducer.app.isCreatingGenProv,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
  {
    getUserAcknowledgement,
    createUserAcknowledgement,
  },
  dispatch,
);

export default connect(mapStateToProps, mapDispatchToProps)(SelectMemberForm);
